import { IS_IOS } from "./environmentDetector";
import { downloadBlob } from "./utils";

/**
 * Save a file the best way the current platform allows.
 *
 * On iOS the HTML `download` attribute does NOT save silently — WebKit opens a
 * full-screen file-preview screen, and in a standalone PWA backing out of it
 * reloads the whole app. The native **share sheet** (Web Share API, files)
 * avoids both: it's a modal overlay (no navigation → no app reload) and offers
 * the actions users actually want — "Save Image" (→ Photos), "Save to Files",
 * or share to another app. So on iOS — and ONLY where the platform confirms it
 * can share this specific file (`navigator.canShare`) — we hand the blob to
 * `navigator.share`.
 *
 * Everywhere else (desktop, Android) the plain anchor download already saves
 * silently and is what users expect, so we keep it. It's also the fallback when
 * the share is unavailable (large/unsupported file) or fails, so a download is
 * always obtainable. iOS-only by design — see [[keep-it-simple]]: this targets
 * the one platform where the anchor is broken and leaves the rest untouched.
 */
export async function saveFile(blob: Blob, filename: string): Promise<void> {
  const file = new File([blob], filename, {
    type: blob.type || "application/octet-stream",
  });

  if (IS_IOS && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file] });
      return;
    } catch (err) {
      // The user dismissed the sheet — a completed interaction, not a failure.
      // Must NOT also fire the anchor, or they'd get a surprise download.
      if (err instanceof DOMException && err.name === "AbortError") {
        return;
      }
      // Any real failure: fall through to the anchor so the file still saves.
    }
  }

  downloadBlob(blob, filename);
}
